/**
 * Issue Detail Section
 *
 * Renders issue context inside an expanded timeline card:
 * severity badge, issue type, blocks_shipment flag, description,
 * recommended actions, financial/delay impact.
 */

import type { IssueDetail } from '@/hooks/api/use-issue-detail';
import { useIssueTypeLabel } from '@/hooks/api/use-issue-types';
import { Badge } from '@/components/base/badges/badges';
import { Clock, CurrencyDollar } from '@untitledui/icons';

const severityColor = {
    critical: 'error',
    high: 'warning',
    medium: 'blue',
    low: 'gray',
} as const;

function Skeleton({ className }: { className?: string }) {
    return <div className={`animate-pulse rounded bg-secondary ${className ?? ''}`} />;
}

interface IssueDetailSectionProps {
    issueDetail: IssueDetail | undefined;
    isLoading: boolean;
}

export function IssueDetailSection({ issueDetail, isLoading }: IssueDetailSectionProps) {
    const getLabel = useIssueTypeLabel();
    if (isLoading) {
        return (
            <div className="mt-3 space-y-2 border-t border-secondary pt-3">
                <div className="flex gap-2">
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="h-5 w-24" />
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
            </div>
        );
    }

    if (!issueDetail) return null;

    return (
        <div className="mt-3 space-y-3 border-t border-secondary pt-3">
            {/* Severity + Type + Blocks badge */}
            <div className="flex flex-wrap items-center gap-2">
                <Badge
                    color={severityColor[issueDetail.severity] ?? 'gray'}
                    size="sm"
                >
                    {issueDetail.severity}
                </Badge>
                <Badge color="gray" size="sm">
                    {getLabel(issueDetail.issue_type)}
                </Badge>
                {issueDetail.blocks_shipment && (
                    <Badge color="error" size="sm">
                        Blocks shipment
                    </Badge>
                )}
            </div>

            {/* Full description */}
            {issueDetail.description && (
                <p className="text-sm text-secondary">{issueDetail.description}</p>
            )}

            {/* Root cause */}
            {issueDetail.root_cause && (
                <div>
                    <p className="text-xs font-medium text-tertiary">Root cause</p>
                    <p className="text-sm text-secondary">{issueDetail.root_cause}</p>
                </div>
            )}

            {/* Recommended actions */}
            {issueDetail.recommended_actions && issueDetail.recommended_actions.length > 0 && (
                <div>
                    <p className="text-xs font-medium text-tertiary mb-1">Recommended actions</p>
                    <ul className="space-y-1">
                        {issueDetail.recommended_actions.map((action, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-sm text-secondary">
                                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-tertiary" />
                                {action}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Impact metrics */}
            {(issueDetail.estimated_delay_hours != null || issueDetail.financial_impact_usd != null) && (
                <div className="flex flex-wrap gap-4">
                    {issueDetail.estimated_delay_hours != null && (
                        <div className="flex items-center gap-1.5 text-xs text-tertiary">
                            <Clock className="h-3.5 w-3.5" />
                            <span>~{issueDetail.estimated_delay_hours}h delay</span>
                        </div>
                    )}
                    {issueDetail.financial_impact_usd != null && (
                        <div className="flex items-center gap-1.5 text-xs text-tertiary">
                            <CurrencyDollar className="h-3.5 w-3.5" />
                            <span>${issueDetail.financial_impact_usd.toLocaleString()} impact</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
